import { BontonPanel } from './../../../../environments/bonton-panel';
import { DateTime } from 'luxon';
import { Router } from '@angular/router';
import { Component, OnInit, ViewChild } from '@angular/core';
import { NgClass, AsyncPipe, DatePipe } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ReplaySubject, Subject, debounceTime, distinctUntilChanged, filter, forkJoin, of, switchMap, takeUntil } from 'rxjs';
import { MatButtonModule } from '@angular/material/button';
import { CacheService } from 'app/services/cache.service';
import { MatDatepickerModule } from '@angular/material/datepicker';
// MatDialogModule intentionally removed from imports to reduce unnecessary modules
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
// MatRadioModule removed: not used in template for bus
import { MatSelect, MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { BusesService } from 'app/services/buses.service';
import { ToasterService } from 'app/services/toaster.service';
// Slick carousel removed: not used in bus template
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { LuxonDateAdapterService } from 'app/services/LuxonDateAdapter.service';
import { CommonUtils, MY_DATE_FORMATS } from 'app/utils/commonutils';
import { SettingsService } from 'app/services/settings.service';
import { themes } from 'app/utils/const';
// CommanService removed: not used by this component
import { ValidationService } from 'app/services/validation.service';
import { VideoHelpService } from 'app/services/VideoHelp.service';
import { CommanService } from 'app/services/comman.service';

@Component({
    selector: 'app-bus-main',
    templateUrl: './main.component.html',
    standalone: true,
    imports: [
    NgClass,
    DatePipe,
    AsyncPipe,
    FormsModule,
    ReactiveFormsModule,
    MatIconModule,
    MatTooltipModule,
    MatButtonModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
    NgxMatSelectSearchModule
],
    providers: [
        { provide: DateAdapter, useClass: LuxonDateAdapterService },
        { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS },
    ],
})
export class MainComponent implements OnInit {

    @ViewChild('fromCitySelect') fromCitySelect: MatSelect;
    @ViewChild('toCitySelect') toCitySelect: MatSelect;

    bontonPanel = BontonPanel
    formGroup: FormGroup;
    fromCityList = new ReplaySubject<any[]>();
    toCityList = new ReplaySubject<any[]>();
    minDate = new Date();
    recentBusSearchList: any;
    themes = themes;

    // Carousel and placeholder data removed — recent search list driven by backend

    source_id: any;
    destination_id: any;
    _unsubscribeAll: Subject<any> = new Subject<any>();
    settings: any = {}
    wlSettingsDetails: any = {};
    busData: any = {};
    isFirstCall: boolean = true;
    isFirstCallTo: boolean = true;
    isFirstService : boolean =  CommonUtils.getB2COrderByUrl() === 'bus';
    // Field selected by UI templates for highlighting currently active input (from/to/date)
    selectedField: string = '';

    constructor(
        // Router used for navigation
        private router: Router,
        // Form builder for creating and updating the reactive form
        private builder: FormBuilder,
        // Cache service used to persist and broadcast filter changes
        private cacheService: CacheService,
        // Fetch buses/cities/recent searches
        private busesService: BusesService,
        // Holds application settings and triggers SEO updates
        private settingService: SettingsService,
        // Toaster service for user-facing messages
        private alertService: ToasterService,
        private commanService: CommanService,
        private validationService: ValidationService,
        private videoHelpService: VideoHelpService
    ) {
        this.settingService.settings.pipe(takeUntil(this._unsubscribeAll)).subscribe(settings => {
            this.settings = settings;
            this.wlSettingsDetails = settings;
            this.busData = this.wlSettingsDetails?.service_search_sections?.find(x => x.section_for.toLowerCase() == "bus");
            this.settingService.updateSeoPageMeta('bus'); // Seo Page Meta Details Updates
        });
    }

    /**
     * Component initialization
     * - Builds form
     * - Populates city lists
     * - Restores saved filters
     */
    ngOnInit(): void {
        this.formGroup = this.builder.group({
            sourceId: [''],
            destinationId: [''],
            fromCityfilter: [''],
            toCityfilter: [''],
            fromCityList: [],
            toCityList: [],
            dateOfJourny: [DateTime.fromJSDate(new Date()).toFormat('yyyy-MM-dd')],
        });

        if (this.bontonPanel.isB2B()) {

            this.getRecentBusSearch();
        }

        const oldJSON = this.getSavedFilters();
        if (!oldJSON.sourceId) {
            this.busesService.getBusCityCombo('').pipe(takeUntil(this._unsubscribeAll)).subscribe({
                next: (res) => {
                    this.fromCityList.next(res);
                    this.toCityList.next(res);
                    if (!oldJSON.sourceId) {
                        this.formGroup.get('sourceId').patchValue(res[0]);
                        this.formGroup.get('destinationId').patchValue(res[0]);
                        this.formGroup.get('fromCityfilter').patchValue('Mumbai')
                        this.formGroup.get('toCityfilter').patchValue('Pune')
                    }

                }, error: (err) => {
                    this.alertService.showToast('error', err);
                }
            });
        }

        setTimeout(() => {
            if (oldJSON.sourceId) {
                if (new Date(oldJSON.dateOfJourny) < new Date())
                    oldJSON.dateOfJourny = DateTime.fromJSDate(new Date()).toFormat('yyyy-MM-dd')
                this.formGroup.patchValue(oldJSON)
                this.isFirstCall = false;
                this.isFirstCallTo = false;
                this.formGroup.get('fromCityfilter').patchValue(oldJSON.sourceId.city)
                this.formGroup.get('toCityfilter').patchValue(oldJSON.destinationId.city)

            }
        }, 500);

        // setup search and subscription for fromCityfilter
        this.setupCityFilter('fromCityfilter', this.fromCityList, 'sourceId', 'isFirstCall');

        // setup search and subscription for toCityfilter
        this.setupCityFilter('toCityfilter', this.toCityList, 'destinationId', 'isFirstCallTo');

    }

    /**
     * Get saved filters from localStorage (safe wrapper)
     * @returns saved filters object or empty object
     */
    private getSavedFilters(): any {
        try {
            return JSON.parse(localStorage.getItem('bus-filters') || '{}');
        } catch (e) {
            return {};
        }
    }

    /**
     * Setup value changes subscription for a city filter control.
     * This consolidates duplicate logic for from and to search boxes.
     * @param filterControlName - Name of the filter control on the form
     * @param subject - ReplaySubject to push results into
     * @param defaultControlName - Form control to set default selected value
     * @param isFirstFlagPropName - Name of the property that tracks first call
     */
    private setupCityFilter(filterControlName: string, subject: ReplaySubject<any[]>, defaultControlName: string, isFirstFlagPropName: 'isFirstCall' | 'isFirstCallTo'): void {
        this.formGroup.get(filterControlName).valueChanges.pipe(
            filter(x => !!x),
            debounceTime(400),
            distinctUntilChanged(),
            switchMap((value: string) => {
                return value ? this.busesService.getBusCityCombo(value) : of([]);
            }),
            takeUntil(this._unsubscribeAll)
        ).subscribe({
            next: (res) => {
                if (res) {
                    subject.next(res);
                }
                const oldJSON = this.getSavedFilters();
                if (!oldJSON.sourceId && this[isFirstFlagPropName]) {
                    // set a default only for initial call
                    this.formGroup.get(defaultControlName).patchValue(res[0]);
                    this[isFirstFlagPropName] = false;
                }
            },
            error: (err) => {
                this.alertService.showToast('error', err);
            }
        });
    }

    /**
     * Open a new browser tab with provided URL
     * @param str - URL to open
     */
    openNewWindow(str: string) {
        window.open(str, '_blank')
    }

    /**
     * Component cleanup and unsubscribes from all active subscriptions
     */
    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
        // complete subjects to avoid memory leaks and notify observers
        try { this.fromCityList.complete(); } catch (e) { }
        try { this.toCityList.complete(); } catch (e) { }
    }

    /**
     * Fetch list of user's recent bus searches.
     */
    getRecentBusSearch() {
        this.busesService.busRecentSearch().pipe(takeUntil(this._unsubscribeAll)).subscribe((res: any) => {
            this.recentBusSearchList = res;
        });
    }

    /**
     * Handle selection of a recent search result and navigate
     * @param val - Selected recent search value
     */
    recentSearch(val): void {
        val.text = 'recentSearch';
        this.detail(val);
    }

    /**
     * Switch from and to fields including their result lists
     */
    switch(): void {
        const from = this.formGroup.get('sourceId').value;
        const to = this.formGroup.get('destinationId').value;
        this.formGroup.get('sourceId').patchValue(to);
        this.formGroup.get('destinationId').patchValue(from);
        var temptoList = this.toCityList;
        this.toCityList = this.fromCityList
        this.fromCityList = temptoList;
    }

    /**
     * Compare function for mat-select used by [compareWith]
     * @param v1 - first value
     * @param v2 - second value
     */
    public compareWith(v1: any, v2: any) {
        return v1 && v2 && v1.cityId === v2.cityId;
    }

    /**
     * Retrieve city metadata for source/destination based on given ids
     * @param value - object containing source/destination filters and ids
     * @param callback - optional callback to run after successful results
     */
    filtercity(value, callback?: () => void) {
        forkJoin([
            this.busesService.getBusCityCombo(value.source),
            this.busesService.getBusCityCombo(value.destination)
        ]).pipe(takeUntil(this._unsubscribeAll)).subscribe({
            next: ([sourceCities, destinationCities]) => {
                this.source_id = sourceCities.find(x => x.cityId === value.source_id);
                this.destination_id = destinationCities.find(x => x.cityId === value.destination_id);
                callback?.();
            },
            error: (err) => {
                this.alertService.showToast('error', err)
            }
        });
    }

    /**
     * Trigger search logic and navigation based on selected values or a recent search
     * @param val - Optional recent search object to navigate to
     */
    detail(val?): void {
        const json = this.formGroup.getRawValue();
        json.dateOfJourny = DateTime.fromJSDate(new Date(json.dateOfJourny)).toFormat('yyyy-MM-dd');

        // previous approach replaced: switched to route including source/destination
        if (val?.text === 'recentSearch') {
            this.filtercity(val, () => {
                json.sourceId = this.source_id;
                const sourceCity = this.source_id?.city + '_' + this.source_id?.state;
                json.destinationId = this.destination_id;
                const DestinationCity = this.destination_id?.city + '_' + this.destination_id?.state;
                json.dateOfJourny = DateTime.fromJSDate(new Date(val.dateOfOrg)).toFormat('yyyy-MM-dd');
                this.cacheService.MainFilterChanged(json, 'bus-filters');
                let navigateStr = this.buildNavigateRoute(sourceCity, DestinationCity, json.dateOfJourny);
                this.router.navigate([navigateStr]);
            });
        } else {

            this.cacheService.MainFilterChanged(json, 'bus-filters');
            const sourceCity = json.sourceId?.city + '_' + json.sourceId?.state;
            const DestinationCity = json.destinationId?.city + '_' + json.destinationId?.state;
            let navigateStr = this.buildNavigateRoute(sourceCity, DestinationCity, json.dateOfJourny);
            this.router.navigate([navigateStr]);
        }
    }

    /**
     * Helper to create the route for navigation
     * @param sourceCity - source city string
     * @param destCity - destination city string
     * @param date - date string (yyyy-MM-dd)
     * @returns path to navigate to
     */
    private buildNavigateRoute(sourceCity: string, destCity: string, date: string): string {
        const route = '/bus/detail/' + sourceCity + '/' + destCity + '/' + date;
        return this.validationService.replaceSpaceToHyphen(route);
    }

    // Destination Select
    /**
     * Open the From mat-select dropdown programmatically
     */
    openfromCitySelect() {
        this.fromCitySelect.open();
    }

    // Destination Select
    /**
     * Open the To mat-select dropdown programmatically
     */
    opentoCitySelect() {
        this.toCitySelect.open();
    }

    helpVideo(sectionName: string) {
        this.videoHelpService.helpVideo(sectionName);
    }
}
